import { errorMessageMapper } from '@/consts/api-error.const';
import {
  ApiError,
  ApiErrorResponse,
  ValidationErrorResponse
} from '@/models/common.model';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import camelCase from 'lodash/camelCase';

export const checkErrorStatus = (
  error: FetchBaseQueryError | SerializedError | null | undefined,
  status: number
): boolean => {
  if (!error || !('data' in error)) {
    return false;
  }

  return error.status === status;
};

export const isNotFoundError = (
  error?: FetchBaseQueryError | SerializedError | null
): boolean => {
  if (!error || !('data' in error)) {
    return false;
  }

  return error.status === 404;
};

export const isValidationError = (
  error?: FetchBaseQueryError | SerializedError | null
): boolean => {
  if (!error || !('data' in error)) {
    return false;
  }

  return (
    error.status === 422 &&
    Array.isArray(((error as any).data as ValidationErrorResponse).details)
  );
};

export const parseValidationError = (
  error?: FetchBaseQueryError | SerializedError
): ValidationErrorResponse['details'] => {
  if (!error || !isValidationError(error)) {
    return [];
  }

  const parsePropertyName = (propertyName: string): string =>
    propertyName
      .split('.')
      .map((part) => camelCase(part))
      .join('.');

  return ((error as any).data as ValidationErrorResponse).details.map(
    (detail) => ({ ...detail, property: parsePropertyName(detail.property) })
  );
};

export const parseApiError = (
  error?: FetchBaseQueryError | SerializedError
): ApiError => {
  if (!error || !('data' in error)) {
    return {
      status: 500,
      code: undefined,
      message: undefined,
      displayMessage: undefined
    };
  }

  const code = (error.data as ApiErrorResponse).code;
  const message = (error.data as ApiErrorResponse).message;
  const meta = (error.data as ApiErrorResponse).meta;

  return {
    status: error.status as number,
    code: code,
    message: message,
    displayMessage: code && errorMessageMapper[code],
    meta: meta
  };
};
